import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  IconButton,
  Collapse,
  Chip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  AccountBalance as FinanceIcon,
  Inventory as InventoryIcon,
  Assessment as ReportsIcon,
  FactCheck as FactCheckIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
  Person as PersonIcon,
  GroupWork as RoleIcon,
  Shield as PermissionIcon,
  AttachMoney as TransactionIcon,
  TrendingUp as AssetIcon,
  ChevronLeft as ChevronLeftIcon,
  Menu as MenuIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 280;

const AdminNavigationDrawer = ({ open, onToggle, mobile = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasPermission, hasModuleAccess } = useAuth();
  const [expandedSections, setExpandedSections] = useState({
    users: false,
    finance: false,
    inventory: false,
    analytics: false,
    reports: false
  });

  const handleSectionToggle = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  // Definir itens do menu baseado nas permissões
  const menuItems = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      module: null // Dashboard é sempre acessível
    },
    {
      id: 'users',
      title: 'Gestão de Usuários',
      icon: <PeopleIcon />,
      module: 'users',
      expandable: true,
      children: [
        {
          id: 'user-list',
          title: 'Usuários',
          icon: <PersonIcon />,
          path: '/admin/users',
          permission: 'users.read'
        },
        {
          id: 'role-management',
          title: 'Cargos e Funções',
          icon: <RoleIcon />,
          path: '/admin/roles',
          permission: 'roles.read'
        },
        // {
        //   id: 'permissions',
        //   title: 'Permissões',
        //   icon: <PermissionIcon />,
        //   path: '/admin/permissions',
        //   permission: 'roles.manage'
        // }
      ]
    },
    {
      id: 'finance',
      title: 'Gestão Financeira',
      icon: <FinanceIcon />,
      module: 'finance',
      expandable: true,
      children: [
        {
          id: 'transactions',
          title: 'Transações',
          icon: <TransactionIcon />,
          path: '/admin/finance/transactions',
          // Mostrar se tiver ALL ou SELF
          permission: ['finance.transactions.read.all', 'finance.transactions.read.self']
        },
        {
          id: 'asset-edit-requests',
          title: 'Solicitações de Ativos',
          icon: <FactCheckIcon />,
          path: '/admin/finance/asset-edit-requests',
          permission: 'finance.transactions.verify'
        },
        {
          id: 'financial-analytics',
          title: 'Analytics Financeiro',
          icon: <AnalyticsIcon />,
          path: '/admin/finance/analytics',
          permission: 'analytics.finance.read'
        }
      ]
    },
    {
      id: 'inventory',
      title: 'Controle de Estoque',
      icon: <InventoryIcon />,
      module: 'inventory',
      expandable: true,
      children: [
        {
          id: 'assets',
          title: 'Ativos/Prêmios',
          icon: <AssetIcon />,
          path: '/admin/assets',
          permission: 'inventory.assets.read'
        },
        {
          id: 'inventory-analytics',
          title: 'Analytics de Estoque',
          icon: <AnalyticsIcon />,
          path: '/admin/inventory/analytics',
          permission: 'analytics.inventory.read'
        }
      ]
    },
    {
      id: 'analytics',
      title: 'Análises',
      icon: <AnalyticsIcon />,
      module: 'analytics',
      expandable: true,
      children: [
        {
          id: 'manual-sales-performance',
          title: 'Desempenho Manual',
          icon: <ReportsIcon />,
          path: '/admin/analytics/manual-sales-performance',
          permission: 'analytics.manual_sales_performance.read'
        }
      ]
    },
    // {
    //   id: 'analytics',
    //   title: 'Analytics & BI',
    //   icon: <ReportsIcon />,
    //   path: '/dashboard',
    //   module: 'analytics'
    // },
    // {
    //   id: 'settings',
    //   title: 'Configurações',
    //   icon: <SettingsIcon />,
    //   path: '/admin/settings',
    //   module: 'system'
    // }
  ];

  // Filtrar itens baseado nas permissões do usuário
  const getFilteredMenuItems = () => {
    // Verificar se as funções de permissão estão disponíveis e o usuário está carregado
    if (!user || !hasModuleAccess || !hasPermission) {
      return menuItems.filter(item => !item.module); // Só mostra dashboard
    }
    
    return menuItems.filter(item => {
      // Se não tem módulo definido, é sempre acessível (dashboard)
      if (!item.module) return true;
      
      // Verifica acesso ao módulo
      return hasModuleAccess(item.module);
    }).map(item => {
      if (item.children) {
        const filteredChildren = item.children.filter(child => {
          if (!child.permission) return true;
          // Permitir array de permissões (qualquer uma)
          if (Array.isArray(child.permission)) {
            return child.permission.some((p) => hasPermission(p));
          }
          return hasPermission(child.permission);
        });
        return { ...item, children: filteredChildren };
      }
      return item;
    });
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isParentActive = (item) => {
    if (item.path && isActive(item.path)) return true;
    if (item.children) {
      return item.children.some(child => isActive(child.path));
    }
    return false;
  };

  const getRoleBadgeColor = (userRole) => {
    const roleColors = {
      'superadmin': 'error',
      'admin': 'primary',
      'gestor': 'secondary',
      'funcionario_financeiro': 'info',
      'funcionario_estoque': 'success'
    };
    return roleColors[userRole] || 'default';
  };

  const getRoleLabel = (userRole) => {
    const roleLabels = {
      'superadmin': 'Super Admin',
      'admin': 'Administrador',
      'gestor': 'Gestor',
      'funcionario_financeiro': 'Financeiro',
      'funcionario_estoque': 'Estoque'
    };
    return roleLabels[userRole] || userRole;
  };

  const filteredMenuItems = getFilteredMenuItems();

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header do drawer */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
            Administração
          </Typography>
          <IconButton onClick={onToggle} size="small">
            <ChevronLeftIcon />
          </IconButton>
        </Box>
        
        {user && (
          <Box>
            <Typography variant="body2" color="text.secondary" noWrap>
              {user.full_name}
            </Typography>
            <Box display="flex" alignItems="center" mt={0.5}>
              <Chip
                label={getRoleLabel(user.role)}
                color={getRoleBadgeColor(user.role)}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Lista de navegação */}
      <List sx={{ flexGrow: 1, pt: 1 }}>
        {filteredMenuItems.map((item) => (
          <React.Fragment key={item.id}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  if (item.expandable) {
                    handleSectionToggle(item.id);
                  } else if (item.path) {
                    handleNavigation(item.path);
                  }
                }}
                selected={isParentActive(item)}
                sx={{
                  minHeight: 44,
                  px: 2,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.title}
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
                {item.expandable && (
                  expandedSections[item.id] ? <ExpandLess /> : <ExpandMore />
                )}
              </ListItemButton>
            </ListItem>

            {/* Subitens expandíveis */}
            {item.expandable && item.children && (
              <Collapse in={expandedSections[item.id]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.children.map((child) => (
                    <ListItem key={child.id} disablePadding>
                      <ListItemButton
                        onClick={() => handleNavigation(child.path)}
                        selected={isActive(child.path)}
                        sx={{
                          pl: 6,
                          minHeight: 40,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            '& .MuiListItemIcon-root': {
                              color: 'primary.contrastText',
                            },
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                            },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {child.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={child.title}
                          primaryTypographyProps={{ fontSize: '0.8125rem' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" display="block">
          BIsys
        </Typography>
        <Typography variant="caption" color="text.secondary">
          v3.9.0 - Painel de Administração
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Drawer adaptativo: temporary em mobile, persistent no desktop */}
      <Drawer
        variant={mobile ? 'temporary' : 'persistent'}
        ModalProps={{ keepMounted: true }}
        anchor="left"
        open={open}
        onClose={onToggle}
        sx={{
          width: open ? drawerWidth : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Botão para abrir drawer quando fechado */}
      {!open && (
        <IconButton
          onClick={onToggle}
          sx={{
            position: 'fixed',
            top: 80,
            left: 16,
            zIndex: 1300,
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}
    </>
  );
};

export default AdminNavigationDrawer;